import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  CashSource,
  CreateExpenseInput,
  CreateIncomeInput,
  DrawerCurrentDto,
  FinanceEntryKind,
  FinanceListResponse,
} from '@gc/shared';

import { api, ApiError } from '@/lib/api';

export interface FinanceFilters {
  from: string;
  to: string;
  search: string;
}

/** Path segment per kind — the API mirrors the two tables rather than merging them. */
const PATH: Record<FinanceEntryKind, string> = {
  EXPENSE: '/expenses',
  INCOME: '/incomes',
};

function toQuery(filters: FinanceFilters): string {
  const params = new URLSearchParams();
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.search.trim()) params.set('search', filters.search.trim());
  const query = params.toString();
  return query ? `?${query}` : '';
}

/**
 * The list for one kind. A response is kept WITH the kind it was fetched for
 * and handed out only while that is still the kind on screen: an Expense row
 * left showing under the Income tab while Incomes loaded once sent
 * `DELETE /incomes/<an expense's id>` (its button routed by the tab; it now
 * routes by the row's own `kind` too). Only the latest request may land, for
 * the same reason — a slow Expenses answer arriving after the Incomes one
 * would put the wrong rows back under the tab.
 */
export function useFinance(kind: FinanceEntryKind, filters: FinanceFilters) {
  const [loaded, setLoaded] = useState<{
    kind: FinanceEntryKind;
    response: FinanceListResponse;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latest = useRef(0);

  const load = useCallback(async () => {
    const request = ++latest.current;
    setLoading(true);
    try {
      const response = await api.get<FinanceListResponse>(`${PATH[kind]}${toQuery(filters)}`);
      if (request !== latest.current) return;
      setLoaded({ kind, response });
      setError(null);
    } catch (caught) {
      if (request !== latest.current) return;
      setError(caught instanceof ApiError ? caught.message : 'Could not load entries.');
    } finally {
      if (request === latest.current) setLoading(false);
    }
  }, [kind, filters.from, filters.to, filters.search]);

  useEffect(() => {
    void load();
  }, [load]);

  const data = loaded?.kind === kind ? loaded.response : null;
  return { data, loading, error, reload: load };
}

export function createEntry(kind: FinanceEntryKind, input: CreateExpenseInput | CreateIncomeInput) {
  return api.post(PATH[kind], input);
}

export function deleteEntry(kind: FinanceEntryKind, id: string) {
  return api.delete(`${PATH[kind]}/${id}`);
}

/**
 * Where a new entry's money came from or went (owner, 2026-10-12). A tap
 * wins — unless it is Drawer while the drawer is known to be off, which the
 * server would refuse. Otherwise: Drawer for staff while the drawer is in use
 * (the ice bought from the till), Outside for the owner (rent from the bank).
 * A drawer whose state is unknown is never the default.
 */
export function cashSourceFor(
  picked: CashSource | null,
  drawerInUse: boolean | null,
  isOwner: boolean,
): CashSource {
  if (picked && !(picked === 'DRAWER' && drawerInUse === false)) return picked;
  return drawerInUse === true && !isOwner ? 'DRAWER' : 'OUTSIDE';
}

/**
 * Whether the cash drawer is in use (`GET /cash-drawer/current`, open to
 * everyone signed in). Null until known, and still null when the read fails:
 * unknown must never pass for off — and the server refuses a Drawer entry
 * while the drawer is off, whatever this says.
 */
export function useDrawerInUse(): boolean | null {
  const [inUse, setInUse] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const current = await api.get<DrawerCurrentDto>('/cash-drawer/current');
        if (!cancelled) setInUse(current.enabled);
      } catch {
        // Left unknown — see above.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return inUse;
}
